import type { CanvasBlock } from '@elva/ui';
import type { FormBlock, FormBlockCatalogEntry, FormBlockConfig, FormFieldOverride } from '../api/types';

const TEXT_VALUE_KEYS = ['label', 'text', 'content', 'title', 'heading', 'html'] as const;
const PLACEHOLDER_KEYS = ['placeholder'] as const;
const HELP_TEXT_KEYS = ['help_text', 'helpText'] as const;
const INTRO_KEYS = ['intro_text', 'custom_intro', 'intro'] as const;
const OUTRO_KEYS = ['outro_text', 'custom_outro', 'outro'] as const;

/** Fallback when catalog entry has no schema (matches FD13 block catalog conventions). */
const FALLBACK_TEXT_KEY_BY_TYPE: Record<string, string | null> = {
  heading: 'label',
  paragraph: 'text',
  rich_text_display: 'text',
  section_break: null,
};

const OPTION_BLOCK_TYPES = new Set(['dropdown', 'radio', 'checkbox', 'checkbox_group']);

/**
 * Optional field features. The server rejects unknown config keys
 * (`unknown_config_key:*`), so each is written only when the catalog entry
 * lists it in `config_keys` — the builder hides the control otherwise.
 */
const FIELD_FEATURE_CONFIG_KEYS = {
  allowOther: 'allow_other',
  columns: 'columns',
  inputFormat: 'input_format',
  accept: 'accept',
} as const;

/**
 * A basic field's optional patient write-back target (`patient.first_name`, …) — the same
 * path the write plan reports for smart-block fields. Written whenever the author picks one:
 * the catalog does not advertise it in `config_keys` yet.
 */
const FIELD_PATH_CONFIG_KEY = 'field_path';

function isPatientOrSmartType(blockType: string): boolean {
  return blockType.startsWith('patient_') || blockType.startsWith('sb_');
}

function isLayoutBlockType(blockType: string): boolean {
  return blockType === 'section_break';
}

export function getCatalogAllowedConfigKeys(entry?: FormBlockCatalogEntry): Set<string> {
  if (!entry) return new Set();

  if (entry.config_keys?.length) {
    return new Set(entry.config_keys.map((item) => (typeof item === 'string' ? item : item.key)));
  }

  if (entry.config_schema && typeof entry.config_schema === 'object') {
    return new Set(Object.keys(entry.config_schema));
  }

  // W14: intentionally do NOT derive allowed *config* keys from `fields`. Field
  // ids identify fields, not block-config keys — treating them as config keys let
  // a smart-block field literally named `custom_intro` route builder intro text
  // into `config.custom_intro` once the (formerly empty) catalog started
  // resolving. Smart-block field edits persist via `config.field_overrides`; the
  // real fix for the old `unknown_field_id` failures is that overrides now key by
  // the server's field ids (the library is server-sourced), not this heuristic.
  return new Set();
}

function pickAllowedKey(candidates: readonly string[], allowed: Set<string>): string | null {
  for (const key of candidates) {
    if (allowed.has(key)) return key;
  }
  return null;
}

function resolveTextConfigKey(blockType: string, allowed: Set<string>): string | null {
  const fromSchema = pickAllowedKey(TEXT_VALUE_KEYS, allowed);
  if (fromSchema) return fromSchema;

  if (isPatientOrSmartType(blockType) || isLayoutBlockType(blockType)) return null;

  if (blockType in FALLBACK_TEXT_KEY_BY_TYPE) {
    return FALLBACK_TEXT_KEY_BY_TYPE[blockType] ?? null;
  }

  return 'label';
}

function readTextValue(config?: FormBlockConfig): string | undefined {
  if (!config) return undefined;
  for (const key of TEXT_VALUE_KEYS) {
    const value = config[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

function readIntroValue(config?: FormBlockConfig): string | undefined {
  if (!config) return undefined;
  for (const key of INTRO_KEYS) {
    const value = config[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

function readOutroValue(config?: FormBlockConfig): string | undefined {
  if (!config) return undefined;
  for (const key of OUTRO_KEYS) {
    const value = config[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

function readOptionsValue(config?: FormBlockConfig): string[] | undefined {
  const raw = config?.options;
  if (!Array.isArray(raw)) return undefined;
  const items = raw.map((item) => String(item).trim()).filter(Boolean);
  return items.length > 0 ? items : undefined;
}

const RESERVED_CONFIG_KEYS = new Set([
  ...TEXT_VALUE_KEYS,
  ...PLACEHOLDER_KEYS,
  ...HELP_TEXT_KEYS,
  ...INTRO_KEYS,
  ...OUTRO_KEYS,
  'options',
  'block_type',
  'overrides',
  'field_overrides',
  'prefill',
  'ai_prompt_mode',
  'custom_label',
  'required',
  FIELD_PATH_CONFIG_KEY,
  ...Object.values(FIELD_FEATURE_CONFIG_KEYS),
]);

function isFieldOverride(value: unknown): value is FormFieldOverride {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function readSmartBlockOverrides(block: FormBlock): Record<string, FormFieldOverride> {
  const fromConfig = block.config?.field_overrides;
  if (fromConfig && Object.keys(fromConfig).length > 0) return fromConfig;

  if (block.overrides && Object.keys(block.overrides).length > 0) return block.overrides;

  const legacy: Record<string, FormFieldOverride> = {};
  for (const [key, value] of Object.entries(block.config ?? {})) {
    if (RESERVED_CONFIG_KEYS.has(key)) continue;
    if (isFieldOverride(value)) legacy[key] = value;
  }
  return legacy;
}

export function serializeFieldOverrides(
  overrides?: CanvasBlock['overrides'],
): Record<string, FormFieldOverride> | undefined {
  if (!overrides) return undefined;

  const serialized: Record<string, FormFieldOverride> = {};
  for (const [fieldId, override] of Object.entries(overrides)) {
    if (!isFieldOverride(override)) continue;
    if (override.visible !== undefined || override.required !== undefined) {
      serialized[fieldId] = override;
    }
  }

  return Object.keys(serialized).length > 0 ? serialized : undefined;
}

/** Build API `config` using block-catalog schema — never invent unknown keys. */
export function buildWireBlockConfig(
  block: CanvasBlock,
  catalogEntry?: FormBlockCatalogEntry,
): FormBlockConfig {
  const blockType = catalogEntry?.type ?? block.typeId;
  const allowed = getCatalogAllowedConfigKeys(catalogEntry);
  const wire: FormBlockConfig = {};

  const ui = block.config ?? {};
  const textValue = ui.label ?? block.title;

  const textKey = resolveTextConfigKey(blockType, allowed);
  if (textKey && textValue) wire[textKey] = textValue;

  const placeholderKey = pickAllowedKey(PLACEHOLDER_KEYS, allowed);
  if (placeholderKey && ui.placeholder) wire[placeholderKey] = ui.placeholder;

  const helpKey = pickAllowedKey(HELP_TEXT_KEYS, allowed);
  if (helpKey && ui.helpText) wire[helpKey] = ui.helpText;

  const introValue = block.customIntro;
  const introKey = pickAllowedKey(INTRO_KEYS, allowed);
  if (introKey && introValue) wire[introKey] = introValue;

  const outroValue = block.customOutro;
  const outroKey = pickAllowedKey(OUTRO_KEYS, allowed);
  if (outroKey && outroValue) wire[outroKey] = outroValue;

  if (block.customLabel) {
    const customLabelKey = allowed.has('custom_label') ? 'custom_label' : null;
    if (customLabelKey) wire[customLabelKey] = block.customLabel;
  }

  // `config_keys` lists only a block's optional extras (allow_other, columns…), never `options`
  // itself — gating on it silently dropped every choice list on save.
  if (OPTION_BLOCK_TYPES.has(blockType) && ui.options?.length) {
    wire.options = ui.options.map((item) => item.trim()).filter(Boolean);
  }

  if (allowed.has(FIELD_FEATURE_CONFIG_KEYS.allowOther) && ui.allowOther) {
    wire[FIELD_FEATURE_CONFIG_KEYS.allowOther] = true;
  }
  if (allowed.has(FIELD_FEATURE_CONFIG_KEYS.columns) && ui.columns && ui.columns > 1) {
    wire[FIELD_FEATURE_CONFIG_KEYS.columns] = ui.columns;
  }
  if (allowed.has(FIELD_FEATURE_CONFIG_KEYS.inputFormat) && ui.inputFormat) {
    wire[FIELD_FEATURE_CONFIG_KEYS.inputFormat] = ui.inputFormat;
  }
  if (allowed.has(FIELD_FEATURE_CONFIG_KEYS.accept) && ui.accept?.trim()) {
    wire[FIELD_FEATURE_CONFIG_KEYS.accept] = ui.accept.trim();
  }

  if (block.source === 'basic' && ui.fieldPath) {
    wire[FIELD_PATH_CONFIG_KEY] = ui.fieldPath;
  }

  // Smart-block field overrides live under config.field_overrides per FD13/FD15.
  if (block.source === 'smart') {
    const fieldOverrides = serializeFieldOverrides(block.overrides);
    if (fieldOverrides) wire.field_overrides = fieldOverrides;
  }

  return wire;
}

export function readUiBlockConfig(
  block: FormBlock,
  source: CanvasBlock['source'],
): CanvasBlock['config'] | undefined {
  if (source === 'smart' || source === 'patient') return undefined;

  const cfg = block.config;
  const label = readTextValue(cfg) ?? block.label;
  const options = readOptionsValue(cfg);
  const fieldPath = cfg?.[FIELD_PATH_CONFIG_KEY];
  const hasFieldPath = typeof fieldPath === 'string' && fieldPath.length > 0;
  if (!label && !cfg?.placeholder && !cfg?.help_text && !options && !hasFieldPath) return undefined;

  const columns = cfg?.[FIELD_FEATURE_CONFIG_KEYS.columns];
  const accept = cfg?.[FIELD_FEATURE_CONFIG_KEYS.accept];
  return {
    ...(label ? { label } : {}),
    required: Boolean(block.is_required ?? block.required ?? cfg?.required ?? false),
    ...(cfg?.placeholder ? { placeholder: cfg.placeholder } : {}),
    ...(cfg?.help_text ? { helpText: cfg.help_text } : {}),
    ...(options ? { options } : {}),
    ...(cfg?.[FIELD_FEATURE_CONFIG_KEYS.allowOther] === true ? { allowOther: true } : {}),
    ...(typeof columns === 'number' ? { columns } : {}),
    ...(cfg?.[FIELD_FEATURE_CONFIG_KEYS.inputFormat] === 'ssn' ? { inputFormat: 'ssn' as const } : {}),
    ...(typeof accept === 'string' && accept ? { accept } : {}),
    ...(hasFieldPath ? { fieldPath } : {}),
  };
}

export function readUiSmartBlockExtras(block: FormBlock): Pick<CanvasBlock, 'customIntro' | 'customOutro' | 'customLabel'> {
  return {
    ...(readIntroValue(block.config) ? { customIntro: readIntroValue(block.config)! } : {}),
    ...(readOutroValue(block.config) ? { customOutro: readOutroValue(block.config)! } : {}),
    ...(block.config?.custom_label ? { customLabel: String(block.config.custom_label) } : {}),
  };
}

export function buildCatalogIndex(catalog: FormBlockCatalogEntry[]): Map<string, FormBlockCatalogEntry> {
  const index = new Map<string, FormBlockCatalogEntry>();
  for (const entry of catalog) {
    index.set(entry.type.toLowerCase(), entry);
  }
  // Aliases never shadow a real type: `sb_patient_address` must not claim `patient_address`.
  for (const entry of catalog) {
    for (const alias of [entry.type.replace(/^sb_/, ''), entry.type.replace(/_/g, '-')]) {
      const key = alias.toLowerCase();
      if (!index.has(key)) index.set(key, entry);
    }
  }
  return index;
}

export function lookupCatalogEntry(
  index: Map<string, FormBlockCatalogEntry>,
  block: CanvasBlock,
): FormBlockCatalogEntry | undefined {
  const candidates = [
    block.typeId,
    block.typeId.replace(/^sb_/, ''),
    block.typeId.replace(/_/g, '-'),
  ];

  for (const candidate of candidates) {
    const match = index.get(candidate.toLowerCase());
    if (match) return match;
  }

  return undefined;
}
